/**
 * Card Component
 *
 * Glassmorphism-styled card container.
 * Uses CSS classes (.glass, .glass-subtle, etc.) that automatically adapt
 * to dark theme inside .theme-admin via CSS overrides in index.css.
 */

import React from 'react';
import type { CSSProperties } from 'react';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'subtle' | 'elevated' | 'solid' | 'accent';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
}

const paddingClasses = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

const glassClassMap: Record<string, string> = {
  default: 'glass',
  subtle: 'glass-subtle',
  elevated: 'glass-elevated',
  accent: 'glass-accent',
  solid: '',
};

const solidStyle: CSSProperties = {
  background: 'var(--bg-elevated-1)',
  borderRadius: '16px',
  border: '1px solid var(--border-default)',
  boxShadow: 'var(--shadow-md)',
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      variant = 'default',
      padding = 'md',
      hover = false,
      className = '',
      style,
      ...props
    },
    ref
  ) => {
    const glassClass = glassClassMap[variant] || '';

    const hoverClasses = hover
      ? 'transition-all hover:scale-[1.01] cursor-pointer'
      : '';

    return (
      <div
        ref={ref}
        className={`${glassClass} ${paddingClasses[padding]} ${hoverClasses} ${className}`.trim()}
        style={variant === 'solid' ? { ...solidStyle, ...style } : style}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';
